<template>
  <div>
    <v-alert type="info" dense text>
      Tests prove the numbers are right. <b>Fixture cases</b> run the report on a small made-up dataset (rolled back afterwards) and compare to the answers you worked out by hand.
      <b>Reconciliations</b> are queries that return the rows that <i>break</i> a rule (e.g. "payments exceed the invoice"); zero rows = pass. They run on the fixture data and on your live data.
    </v-alert>

    <v-row>
      <v-col cols="12" lg="7">
        <div class="text-subtitle-2 mb-1">Test definitions (JSON)</div>
        <div class="text-caption grey--text mb-1">
          <code>fixtures</code>: rows per table (e.g. <code>{ "Orders": [ ... ] }</code>) - <code>systemParams</code> optionally override tax rate / transaction-type ids -
          <code>cases</code>: <code>{ name, dataset | info: true, drill?, params, expect: { rows | rowCount } }</code> -
          <code>reconciliations</code>: <code>{ name, description?, sql, params? }</code>
        </div>
        <code-box :value="text" mode="json" :rows="22" :max-height="560" @input="onInput" />
        <v-alert v-if="parseError" type="error" dense text class="mt-2">{{ parseError }}</v-alert>
      </v-col>

      <v-col cols="12" lg="5">
        <div class="d-flex align-center mb-2">
          <div class="text-subtitle-2">Results</div>
          <v-spacer />
          <v-btn small color="primary" :loading="running" :disabled="!!parseError" @click="runTests(false)"><v-icon left small>mdi-test-tube</v-icon>Run tests</v-btn>
          <v-btn small class="ml-2" :loading="running" :disabled="!!parseError || !state.id || dirty" title="Saved reports only" @click="runTests(true)">Include live data</v-btn>
        </div>
        <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>
        <div v-if="results">
          <v-chip small :color="allOk ? 'success' : 'error'" class="mb-2">{{ passed }}/{{ total }} passed</v-chip>
          <div v-for="g in groups" :key="g.title" class="mb-3">
            <div class="text-caption font-weight-bold">{{ g.title }}</div>
            <div v-if="!g.items.length" class="text-caption grey--text">None.</div>
            <div v-for="(t, i) in g.items" :key="i" class="d-flex align-start my-1">
              <v-icon small :color="t.ok ? 'success' : 'error'" class="mr-2 mt-1">{{ t.ok ? 'mdi-check' : 'mdi-close' }}</v-icon>
              <div>
                <div class="text-body-2">{{ t.name }}</div>
                <div v-for="(p, j) in t.problems || []" :key="'p' + j" class="caption error--text">{{ p }}</div>
                <div v-if="t.error" class="caption error--text">{{ t.error }}</div>
                <div v-if="t.violations" class="caption error--text">{{ t.violations }} violation(s), e.g. {{ JSON.stringify(t.sample && t.sample[0]) }}</div>
              </div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { errorText } from '../rbUtil'
import axios from 'axios'
import { mapGetters } from 'vuex'
import CodeBox from './CodeBox.vue'
import { state } from './editorState'

export default {
  name: 'RbTestsTab',
  components: { CodeBox },
  props: { dirty: Boolean },
  data () {
    return { state, text: '', parseError: '', running: false, results: null, error: '' }
  },
  computed: {
    ...mapGetters(['getEndpoint']),
    groups () {
      if (!this.results) return []
      return [
        { title: 'Fixture cases', items: this.results.fixture.cases },
        { title: 'Reconciliations on fixture data', items: this.results.fixture.reconciliations },
        { title: 'Reconciliations on live data', items: this.results.live || [] }
      ]
    },
    all () { return this.groups.flatMap((g) => g.items) },
    total () { return this.all.length },
    passed () { return this.all.filter((x) => x.ok).length },
    allOk () { return this.total > 0 && this.passed === this.total }
  },
  created () {
    this.text = JSON.stringify(state.tpl.tests || {}, null, 2)
  },
  methods: {
    onInput (v) {
      this.text = v
      try {
        state.tpl.tests = JSON.parse(v)
        this.parseError = ''
      } catch (e) {
        this.parseError = `Not valid JSON yet: ${e.message}`
      }
    },
    async runTests (live) {
      this.running = true
      this.error = ''
      try {
        const res = live
          ? await axios.post(`${this.getEndpoint}/api/rb-builder/reports/${state.id}/test`, {})
          : await axios.post(`${this.getEndpoint}/api/rb-builder/test-draft`, { template: state.tpl })
        this.results = res.data.data
      } catch (e) {
        this.results = null
        this.error = errorText(e)
      } finally {
        this.running = false
      }
    }
  }
}
</script>
